import { useRuntimeVariables } from 'app-kit'
import { AxiosResponse } from 'axios'
import imageCompression, { Options } from 'browser-image-compression'
import { AuthedAxiosRequests, useGeckoAuth } from 'features/auth'
import { RawCdfEntry } from 'features/dataLayers'
import { RolodexEntryQueryBody, useRolodexEntries } from 'features/rolodex'
import _ from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  RolodexEntryJson,
  RolodexEntryPatchJson,
  RolodexEntryPutJson,
  RolodexViewRestrictionPatchJson,
} from 'types'

export const THUMBNAIL_FILE_PREFIX = 'thumbnail_'

// ================================================= Hook ================================================
export function useRolodexEdit() {
  const { ROLODEX_URL } = useRuntimeVariables()
  const { authorizedAxios: axios } = useGeckoAuth()

  return useMemo(() => {
    return {
      rolodexPut: (entries: RolodexEntryPutJson[]) =>
        rolodexPut(entries, ROLODEX_URL, axios),
      rolodexPatch: (entries: RolodexEntryPatchJson[]) =>
        rolodexPatch(entries, ROLODEX_URL, axios),
      rolodexDelete: (id: string, deleteFiles: boolean = false) =>
        rolodexDelete(id, deleteFiles, ROLODEX_URL, axios),
      rolodexUploadFile: (entry_id: string, file: Blob, toPath: string) =>
        rolodexUploadFile(entry_id, file, toPath, ROLODEX_URL, axios),
      rolodexUploadCdf: (entry: RawCdfEntry, texture: Blob, dataCsv: Blob) =>
        rolodexUploadCdf(entry, texture, dataCsv, ROLODEX_URL, axios),
      rolodexDeleteFile: (entry_id: string, path: string) =>
        rolodexDeleteFile(entry_id, path, ROLODEX_URL, axios),
      rolodexPatchViewRestriction: (
        restrictionPatch: RolodexViewRestrictionPatchJson
      ) => rolodexPatchViewRestriction(restrictionPatch, ROLODEX_URL, axios),

      /**
       * A wrapper function on top of rolodexUploadFile that attempts to compress an image and create a thumbnail version
       * for use within the application.
       * @param entry_id
       * @param file
       * @param toPath
       * @param compressionOptions
       */
      rolodexUploadImage: (
        entry_id: string,
        file: File,
        toPath: string,
        compressionOptions?: Options
      ) =>
        rolodexUploadImage(
          entry_id,
          file,
          toPath,
          compressionOptions,
          ROLODEX_URL,
          axios
        ),
    }
  }, [ROLODEX_URL, axios])
}

// =========================================== Rolodex Callbacks ============================================
async function rolodexPut(
  entries: RolodexEntryPutJson[],
  RolodexUrl: string,
  axios: AuthedAxiosRequests
): Promise<AxiosResponse> {
  const url = `${RolodexUrl}/api/v2/entries/`
  return axios.put(url, entries, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })
}

async function rolodexPatch(
  entries: RolodexEntryPatchJson[],
  RolodexUrl: string,
  axios: AuthedAxiosRequests
): Promise<AxiosResponse> {
  const url = `${RolodexUrl}/api/v2/entries/`
  return axios.patch(url, entries, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })
}

async function rolodexDelete(
  id: string,
  deleteFiles: boolean = false,
  RolodexUrl: string,
  axios: AuthedAxiosRequests
): Promise<AxiosResponse> {
  const url = `${RolodexUrl}/api/v2/entries/${id}?delete_files=${deleteFiles}`
  return axios.delete(url, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })
}

async function rolodexUploadFile(
  entry_id: string,
  file: Blob,
  toPath: string,
  RolodexUrl: string,
  axios: AuthedAxiosRequests
): Promise<AxiosResponse> {
  const signed_url = await rolodexUploadUrl(entry_id, toPath, RolodexUrl, axios)
  return axios.put(signed_url, file, {
    headers: {
      'Content-Type': 'application/octet-stream',
    },
  })
}

async function rolodexUploadImage(
  entry_id: string,
  file: File,
  toPath: string,
  compressionOptions: Options | undefined,
  RolodexUrl: string,
  axios: AuthedAxiosRequests
): Promise<AxiosResponse> {
  const fullResSignedURL = await rolodexUploadUrl(
    entry_id,
    toPath,
    RolodexUrl,
    axios
  )
  const thumbnailSignedURL = await rolodexUploadUrl(
    entry_id,
    `${THUMBNAIL_FILE_PREFIX}${toPath}`,
    RolodexUrl,
    axios
  )
  if (!compressionOptions)
    compressionOptions = {
      maxSizeMB: 1,
      maxWidthOrHeight: 150,
      useWebWorker: true,
    }

  let compressedImage
  try {
    compressedImage = await imageCompression(file, compressionOptions)
  } catch (e) {
    console.warn(`An error occurred when compressing image! ${e}`)
  }

  if (compressedImage) {
    try {
      await axios.put(thumbnailSignedURL, compressedImage, {
        headers: {
          'Content-Type': 'application/octet-stream',
        },
      })
    } catch (e) {
      console.warn(
        `An error occurred when uploading the compressed image! ${e}`
      )
    }
  }

  return await axios.put(fullResSignedURL, file, {
    headers: {
      'Content-Type': 'application/octet-stream',
    },
  })
}

export async function rolodexDeleteFile(
  entry_id: string,
  path: string,
  rolodexUrl: string,
  axios: AuthedAxiosRequests
): Promise<AxiosResponse> {
  const url = `${rolodexUrl}/api/v2/files/${encodeURIComponent(entry_id)}/${encodeURIComponent(path)}`
  return await axios.delete(url)
}

async function rolodexUploadUrl(
  entry_id: string,
  toPath: string,
  rolodexUrl: string,
  axios: AuthedAxiosRequests
): Promise<string> {
  const url = `${rolodexUrl}/api/v2/files/${entry_id}/${encodeURIComponent(
    toPath
  )}?content_type=application/octet-stream`

  const response = await axios.put(url, undefined, {
    headers: {
      'Content-Type': 'application/octet-stream',
    },
  })
  return response.data
}

// Function to set view restrictions
async function rolodexPatchViewRestriction(
  restrictionPatch: RolodexViewRestrictionPatchJson,
  RolodexUrl: string,
  axios: AuthedAxiosRequests
): Promise<AxiosResponse> {
  const url = `${RolodexUrl}/api/v2/entries/view_restrictions`
  return axios.patch(url, restrictionPatch, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })
}

export function useGetRolodexUploadUrl(): (
  entry_id: string,
  path: string
) => Promise<string> {
  const { ROLODEX_URL: rolodexUrl } = useRuntimeVariables()
  const { authorizedAxios: axios } = useGeckoAuth()
  return useCallback(
    async (entry_id: string, toPath: string) =>
      await rolodexUploadUrl(entry_id, toPath, rolodexUrl, axios),
    [axios, rolodexUrl]
  )
}

const COMPARE_OMIT_FIELDS = ['generation', 'index']

function entriesDiff<T extends RolodexEntryJson>(
  entriesSource: T[],
  entriesTarget: T[]
): { toDelete: string[]; toPatch: string[] } {
  const ids1 = new Set(entriesSource.map((e) => e.id))
  const ids2 = new Set(entriesTarget.map((e) => e.id))

  const toDelete = [...ids2].filter((id) => !ids1.has(id))
  const toPatch = [...ids1].filter((id) => {
    if (toDelete.includes(id)) {
      return false
    }
    const e1 = _.find(entriesSource, (e) => e.id === id)
    const e2 = _.find(entriesTarget, (e) => e.id === id)
    return (
      !e2 ||
      !_.isEqual(
        _.omit(e1, COMPARE_OMIT_FIELDS),
        _.omit(e2, COMPARE_OMIT_FIELDS)
      )
    )
  })
  return { toDelete, toPatch: toPatch }
}

function entriesEqual<T extends RolodexEntryJson>(
  entries1: T[],
  entries2: T[]
): boolean {
  const { toPatch: toPatch, toDelete } = entriesDiff(entries1, entries2)
  return !toPatch.length && !toDelete.length
}

function undefFilter<T extends RolodexEntryJson>(
  val: T | undefined | null
): val is T {
  return val !== undefined && val !== null
}

export type RolodexSyncType<T> = {
  values: ReadonlyArray<T>
  setValues: (vals: ReadonlyArray<T>) => void
  isLoading: boolean
}

export function useRolodexSync<T>(
  queryOrQueries:
    | RolodexEntryQueryBody
    | ReadonlyArray<RolodexEntryQueryBody>
    | undefined,
  toRolodexEntry: (val: T) => RolodexEntryJson,
  fromRolodexEntry: (entry: RolodexEntryJson) => T
): RolodexSyncType<T> {
  const {
    entries: rdEntries,
    isLoading,
    hasEverLoaded,
    forceRefresh,
  } = useRolodexEntries(queryOrQueries)

  const rdEntriesRaw: RolodexEntryJson[] = useMemo(
    () => rdEntries?.map((rdEntry) => rdEntry.raw),
    [rdEntries]
  )

  const [localEntries, _setLocalEntries] = useState<RolodexEntryJson[]>([])

  const { rolodexPatch, rolodexDelete } = useRolodexEdit()

  const [refreshInProgress, setRefreshInProgress] = useState<boolean>(false)

  const setValues: (values: ReadonlyArray<T>) => Promise<void> = useCallback(
    async (values) => {
      // If nothing changed, we can skip the update
      const asRdJson = values.map((v) => toRolodexEntry(v))
      if (entriesEqual(localEntries, asRdJson)) {
        return
      }

      try {
        setRefreshInProgress(true)
        _setLocalEntries(asRdJson)
        const { toDelete, toPatch: toPatchIds } = entriesDiff(
          asRdJson,
          rdEntriesRaw
        )
        const toPatch = toPatchIds
          .map((toPatchId) => _.find(asRdJson, (le) => le.id === toPatchId))
          .filter((val) => undefFilter<RolodexEntryJson>(val))
          .map((val) => val as RolodexEntryJson)
        const promises = [
          ...toDelete.map((id) => rolodexDelete(id, true)),
          toPatch.length ? rolodexPatch(toPatch) : null,
        ]
          .filter((prom) => !!prom)
          .map((prom) => prom as Promise<AxiosResponse>)
        await Promise.all(promises)
        await forceRefresh()
      } finally {
        setRefreshInProgress(false)
      }
    },
    [
      forceRefresh,
      localEntries,
      rdEntriesRaw,
      rolodexDelete,
      rolodexPatch,
      toRolodexEntry,
    ]
  )

  // Bit
  const setLocalEntriesDebounced: (entries: RolodexEntryJson[]) => void =
    useMemo(() => _.debounce(_setLocalEntries, 200), [])

  // Deal with incoming changes from Rolodex
  useEffect(() => {
    async function sync() {
      if (!hasEverLoaded || refreshInProgress) {
        return
      }
      // If we're already in sync, then there's nothing to do
      if (entriesEqual(localEntries, rdEntriesRaw)) {
        return
      }
      // If the Rolodex entries changed, we need to update locally
      setLocalEntriesDebounced(rdEntriesRaw)
    }

    sync()
  }, [
    hasEverLoaded,
    localEntries,
    rdEntriesRaw,
    refreshInProgress,
    setLocalEntriesDebounced,
  ])

  const mappedLocalEntries = useMemo(
    () => localEntries.map((e) => fromRolodexEntry(e)),
    [fromRolodexEntry, localEntries]
  )

  return useMemo(() => {
    return {
      values: mappedLocalEntries,
      setValues,
      isLoading: isLoading || !hasEverLoaded,
    }
  }, [hasEverLoaded, isLoading, mappedLocalEntries, setValues])
}

async function rolodexUploadCdf(
  entry: RawCdfEntry,
  texture: Blob,
  dataCsv: Blob,
  RolodexUrl: string,
  axios: AuthedAxiosRequests
): Promise<AxiosResponse> {
  await rolodexPut([entry], RolodexUrl, axios)
  await rolodexUploadFile(entry.id, texture, 'texture.png', RolodexUrl, axios)
  return rolodexUploadFile(entry.id, dataCsv, 'data.csv', RolodexUrl, axios)
}

function isNotNull<T>(item: T | null): item is T {
  return !!item
}

/**
 * When using rolodex sync, often we'll pass a filtered list of items
 * through to a view. When someone edits something inside that view,
 * we want to write it back, but leave alone any items that were not
 * in the filter.
 *
 * This helper facilitates that - it gives back a setter which passes
 * through any items not in the filter, and passes over any conflicts.
 *
 * There is an example in IndicationTriageExtension.
 *
 * @param allItems the unfiltered list of items.
 * @param filteredItems the filtered list of items sent to the caller of the setter.
 * @param unfilteredSetter the setter to call through to.
 * @returns a function which sets the filtered view, passing through any items from
 * the unfiltered view.
 */
export function useSetFiltered<T extends { id: string }>(
  allItems: ReadonlyArray<T>,
  filteredItems: ReadonlyArray<T>,
  unfilteredSetter: (items: ReadonlyArray<T>) => void
): (items: ReadonlyArray<T>) => void {
  const allIds: ReadonlySet<string> = useMemo(
    () => new Set(allItems.map((item) => item.id)),
    [allItems]
  )
  const filteredIds: ReadonlySet<string> = useMemo(
    () => new Set(filteredItems.map((item) => item.id)),
    [filteredItems]
  )
  return useCallback(
    (toSet: ReadonlyArray<T>) => {
      const toSetIds: ReadonlySet<string> = new Set(
        toSet.map((item) => item.id)
      )
      const newIds = toSetIds.difference(filteredIds)

      // If we added a new ID that's in all but not filtered, that's a conflict
      // Warn, and don't write it
      const addConflicts = newIds.intersection(allIds)
      if (addConflicts.size) {
        console.warn(
          `Conflicting IDs on filtered setter: [${[...addConflicts].join(', ')}]`
        )
      }

      // We iterate through all the items replacing
      // any new ones - this is to preserve the order
      const allItemsUpdated = allItems
        .map((item) => {
          if (!filteredIds.has(item.id)) {
            return item
          }

          // If we're setting an ID that's in the filtered set,
          // we just want to replace the item
          if (toSetIds.has(item.id)) {
            return _.find(toSet, (newItem) => newItem.id === item.id) ?? null
          } else {
            // This was in the fitlered IDs, but it's not in the new set
            // - it was deleted
            return null
          }
        })
        .filter((item) => isNotNull(item))
      const toAddItems = toSet
        .filter((item) => newIds.has(item.id))
        .filter((item) => !addConflicts.has(item.id))

      unfilteredSetter([...allItemsUpdated, ...toAddItems])
    },
    [allIds, allItems, filteredIds, unfilteredSetter]
  )
}
